import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SchemaListUpdate, Table, TablesUpdate } from '@mode/shared/contract-common';
import { EnvironmentFacade } from '@mode/shared/environment/contract';
import { ACCEPT_BASIC_JSON, ADD_DATA_INDEX_AUTH } from '@mode/shared/util-halogen';
import { firstValueFrom, map } from 'rxjs';
import { toSchemaListUpdate, toTable, toTablesUpdate } from './data-index-data-mappers';

type RequestDetails = {
  dataSourceToken: string;
  nodeId?: string;
  page?: number;
};

@Injectable({ providedIn: 'root' })
export class DataIndexClientService {
  private dataIndexBase = 'dataindex'; // Fallback to dataindex if env is not loaded

  constructor(private http: HttpClient, private environmentFacade: EnvironmentFacade) {
    this.environmentFacade.dataIndexBase$.subscribe((dataIndexBase) => (this.dataIndexBase = dataIndexBase));
  }

  public async getSchemas(dataSourceToken: string, page?: number): Promise<SchemaListUpdate> {
    return await firstValueFrom(
      this.getFromDataIndex({ dataSourceToken, page }).pipe(map((response: any) => toSchemaListUpdate(response)))
    );
  }

  public async getSchema(dataSourceToken: string, schemaId: string): Promise<TablesUpdate> {
    return await firstValueFrom(
      this.getFromDataIndex({ dataSourceToken, nodeId: schemaId }).pipe(
        map((response: any) => toTablesUpdate(response))
      )
    );
  }

  public async getTable(dataSourceToken: string, schemaId: string, tableId: string): Promise<Table> {
    return await firstValueFrom(
      this.getFromDataIndex({ dataSourceToken, nodeId: tableId }).pipe(
        map((response: any) => toTable(response, schemaId))
      )
    );
  }

  public async getTables(dataSourceToken: string, schemaId: string, page: number): Promise<TablesUpdate> {
    return await firstValueFrom(
      this.getFromDataIndex({
        dataSourceToken,
        nodeId: schemaId,
        page,
      }).pipe(map((response: any) => toTablesUpdate(response)))
    );
  }

  private getFromDataIndex(requestDetails: RequestDetails) {
    let url = `${this.dataIndexBase}/api/datasource/${requestDetails.dataSourceToken}/node`;

    if (requestDetails.nodeId) {
      url += `/${requestDetails.nodeId}`;
    }

    if (requestDetails.page !== undefined) {
      url += `?page=${requestDetails.page}`;
    }

    const context = new HttpContext();
    context.set(ACCEPT_BASIC_JSON, true);
    context.set(ADD_DATA_INDEX_AUTH, true);

    return this.http.get(url, { context });
  }
}
